import { Component, inject, Inject, OnInit } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { CoreLibModalComponent } from '../modal/modal.component';
import { DialogRef } from '../modal/dialog-ref';
import { DIALOG_DATA } from '../modal/dialog-tokens';
import { UtilService } from 'app/shared/util.service';
import { ProductId } from 'app/shared/enums';
import { TranslateService } from '@ngx-translate/core';

export interface TableHeader {
  label: string;
  translateKey: string;
  cssClass?: string;
  width?: string;
  key?: string; // Identificador para asociar con células
}

export interface CellData {
  value: any;
  tooltip?: string;
  tooltipParams?: any;
  cssClass?: string;
  isPercentage?: boolean;
  colorCondition?: (value: any) => boolean;
  colorClass?: {
    positive: string;
    negative: string;
  };
  type?: 'text' | 'button' | 'percentage';
  buttonClass?: string;
  buttonText?: string;
  buttonTranslateKey?: string;
  onClick?: () => void;
  coverage?: number;
}

export interface TableRow {
  id: string | number;
  translateKey?: string;
  label?: string;
  cssClass?: string;
  cells: { [key: string]: CellData };
}

@Component({
  selector: 'mh-core-lib-email-rate-modal',
  templateUrl: './email-rate-modal.component.html',
  styleUrls: ['./email-rate-modal.component.scss'],
  standalone: false,
})
export class CoreLibEmailRateModalComponent extends CoreLibModalComponent implements OnInit {
  currentUser = this.utilService.getCurrentUser();
  coverageEmailStatus = '';
  coveragePhoneStatus = '';
  readonly PRODUCT_IDS = ProductId;
  translateService = inject(TranslateService);
  tableRows: TableRow[] = [];
  tableHeaders: TableHeader[] = [];
  customImproveText: string = '';
  safeImproveText: SafeHtml = '';
  customImproveIcon: string = '';
  customTitle: string = '';
  customHasA: string = 'followup.summary.emails-pms.modal.has_a';
  customDescription: string = '';
  tooltipDescription: string = '';
  coverage: number;
  showCoverage: boolean = true;
  showWarnMessage: boolean = false;
  blurRowId: string | null = null;
  tableEmptyMessage: string = '';
  constructor(
    protected dialogRef: DialogRef,
    @Inject(DIALOG_DATA) public data: any,
    private utilService: UtilService,
    private sanitizer: DomSanitizer,
  ) {
    super(dialogRef, data);
  }

  ngOnInit(): void {
    this.coverageEmailStatus = this.utilService.qualifyPercent(this.data.pms.coverageEmail);
    this.coveragePhoneStatus = this.utilService.qualifyPercent(this.data.pms.coveragePhone);
    this.customImproveText =  this.translateService.instant( this.data.customImproveText || 'followup.summary.emails-pms.modal.text_improve');
    this.safeImproveText = this.sanitizer.bypassSecurityTrustHtml(this.customImproveText);
    this.customImproveIcon = this.data.customImproveIcon || '🚀';
    this.customTitle = this.data.customTitle || 'followup.summary.emails-pms.title';
    this.customHasA = this.data.customHasA || 'followup.summary.emails-pms.modal.has_a';
    this.customDescription = this.data.customDescription || '';
    this.tooltipDescription = this.data.tooltipDescription || '';
    this.coverage = this.data.coverage;
    this.showCoverage = this.data.coverage !== null && this.data.coverage !== undefined;
    this.showWarnMessage = this.data.showWarnMessage || false;
    this.blurRowId = this.data.blurRowId || null;
    this.tableEmptyMessage = this.data.tableEmptyMessage || '';
    // Inicializar los elementos de la tabla si no se proporcionó una lista
    if (!this.data.tableRows || !this.data.tableRows.length) {
      this.initDefaultTableData();
    } else {
      this.tableRows = this.data.tableRows;
      this.tableHeaders = this.data.tableHeaders || [];
    }
  }

  initDefaultTableData(): void {}

  close() {
    const keyProduct = this.data.productId === this.PRODUCT_IDS.FollowUp ? 'followup' : 'onsite';
    const key = this.data.widgetKey ? this.data.widgetKey : keyProduct + '-datamodal-close';
    this.utilService.ga('event', 'click', key);
    this.dialogRef.close();
  }

  isCoverageValid(coverage: number): boolean {
    return coverage >= 50;
  }

  getKeys(obj: any): string[] {
    return Object.keys(obj);
  }

  getCellValue(cell: CellData): any {
    return cell.value;
  }

  getColorClass(cell: CellData): string {
    if (cell.colorCondition && cell.colorClass) {
      return cell.colorCondition(cell.value) ? cell.colorClass.positive : cell.colorClass.negative;
    }
    return '';
  }

  shouldBlurRow(rowId: string | number): boolean {
    return this.blurRowId !== null && rowId === this.blurRowId;
  }

  onImproveTextClick(event: Event): void {
    const target = event.target as HTMLElement;
    if (target.tagName === 'A') {
      const trackKey = target.getAttribute('data-track');
      if (trackKey) {
        this.utilService.ga('', 'widget_clicked', null, null, {
          product: 'concierge',
          widget_name: 'interaction_rate',
          action: `go-to-config-${trackKey}`,
        });
      }
    }
  }

  get status() {
    return this.data.classes.state.replace('_', '-');
  }

  get type() {
    return this.data.type;
  }

  get tooltipTranslate() {
    return this.data.tooltipTranslate;
  }

  get currentDateFilter() {
    return this.data?.currentFilter?.label;
  }

  get validEmailPercentage() {
    return this.data.validEmailPercentage;
  }

  get guestsWithValidEmail() {
    return this.data.guestsWithValidEmail;
  }

  get totalGuests() {
    return this.data.totalGuests;
  }

  get emailsCount() {
    return this.data.emailsCount;
  }

  get isAdmin() {
    return this.currentUser.isAdmin() || this.currentUser.user_role.toLowerCase() === 'administrator';
  }
}
